import { firstValueFrom } from 'rxjs';
import { ActivityEntityName, DomainType, EntityViewDisplay, getPortalClientSectionRights, getPortalContractorSectionRights, SurfaceType, Task, TaskLinkType, TaskSecurityRight } from '@jms/model';
import { RightsService } from '@jms/utils/util.permissions';
import { EntityDataServiceRaw } from '@workbuddy/services/entities/entity.data.service.raw';
import { registerManifest } from '@workbuddy/services/entities/entity.manifest.registry';
import type { EntityManifestBoard, EntityManifestSurfaces } from '@workbuddy/services/entities/entity.manifest.surface';
import { ListRowV2Renderer } from '@workbuddy/ui/v2/blocks/entity.board/components/board.list/list.row.v2/list.row.v2.renderer';
import type { EntityBoardDisplaySettingsExtension, EntityBoardListNestedConfig, EntityBoardPanelClick, EntityBoardPanelTarget } from '@workbuddy/ui/v2/blocks/entity.board/context';
import type { TaskDetailParams } from './surface/detail/task.detail.model';
import { resolveTaskAssigneeListProperty, resolveTaskDueDateListProperty, resolveTaskEvidenceListIcon, resolveTaskParentProgress, resolveTaskTitleAdornment } from './task.list.presentation';
import { resolveTaskRecordParent } from './task.record.parent';

// Task entity manifest: parent-scoped list presentation, Form create, and Detail edit/preview.

const nestedTaskParentTypes = new Set<TaskLinkType>([TaskLinkType.Task, TaskLinkType.Phase, TaskLinkType.Daily, TaskLinkType.Checklist, TaskLinkType.Compliance, TaskLinkType.LegacyCompliance, TaskLinkType.Item, TaskLinkType.ItemTask]);

/** Structural nesting policy for the list body (not Display Settings UI). */
const taskNestedList: EntityBoardListNestedConfig = {
  parentField: 'parent',
  isNested: ({ parent, parentItem }) => {
    const parentType = (parent as { type?: TaskLinkType } | null)?.type;
    return parentItem != null || (parentType != null && nestedTaskParentTypes.has(parentType));
  },
  showByDefault: true,
  indentByDefault: true,
};

/**
 * Display Settings rows for nested list visibility / indent.
 * Paths write `view.list.showNestedItems` / `indentNestedItems` (board-owned, same as before).
 * No shell special-case — generic contributions only.
 */
const taskNestedDisplaySettings: EntityBoardDisplaySettingsExtension = {
  contributions: [
    {
      id: 'task-nested',
      when: ({ display }) => display === EntityViewDisplay.List,
      controls: [
        {
          kind: 'switch',
          id: 'showNestedItems',
          label: 'Show sub-tasks',
          path: 'list.showNestedItems',
          default: true,
        },
        {
          kind: 'switch',
          id: 'indentNestedItems',
          label: 'Indent sub-tasks',
          path: 'list.indentNestedItems',
          default: true,
          disabled: values => values['showNestedItems'] !== true,
        },
      ],
    },
  ],
};

/** A Daily parent only groups its dated instances, which carry the evidence and assignee, so it opens no Task Details (WBS-12103). */
function resolveTaskPanelTarget({ item, rowId }: EntityBoardPanelClick<Task | null>): EntityBoardPanelTarget | null {

  if (item?.daily && !item.instance) return null;
  return { entity: ActivityEntityName.Task, id: rowId };
}

const taskSurfaces: EntityManifestSurfaces<Task> = {
  [SurfaceType.Form]: () => import('./surface/create/task.create.surface').then(m => m.taskCreateFormSurface),
  [SurfaceType.Detail]: () => import('./surface/detail/task.detail.surface').then(m => m.taskDetailSurface),
};

const taskBoard: EntityManifestBoard = {
  // List matches the Jobs IssueTable design (grouped rows, left title + right chips).
  supportedDisplays: [EntityViewDisplay.List],
  defaults: {
    panel: {
      kind: 'quickview',
      // Task Details stays at the side so the list narrows with it; the narrow-board dialog could not be seen (WBS-13010).
      popout: false,
      width: 440,
      minWidth: 360,
      maxWidth: 520,
      resolveTarget: resolveTaskPanelTarget,
    },
    header: {
      title: 'Tasks',
      showTitle: true,
      showSearch: true,
      showPaginator: false,
      showViewOptions: false,
      defaultViewId: `${ActivityEntityName.Task}-job-tasks-list`,
    },
    // Linear-style list options. Display properties → view.list.fields (right chips).
    // Allow order = chip / row appearance order; left slots (name/status) stay structural.
    list: {
      // Linear appearance: middle chips first, then assignee → created → updated (trailing).
      displayProperties: {
        allow: ['tags', 'type', 'target', 'evidence', 'dueDate', 'assignee', 'createdAt', 'updatedAt'],
      },
      // One-line v2 row (WBS-13010); reverting is these four lines back to the v1 row and evidence chip.
      rowRenderer: ListRowV2Renderer,
      rowHeight: 40,
      fixedProperties: ['evidence'],
      propertyPresentations: { evidence: resolveTaskEvidenceListIcon, dueDate: resolveTaskDueDateListProperty(), assignee: resolveTaskAssigneeListProperty },
      titleAdornment: resolveTaskTitleAdornment,
      parentProgress: resolveTaskParentProgress,
      // Structural nesting only — show/indent switches live on displaySettings.contributions.
      nested: taskNestedList,
    },
    displaySettings: taskNestedDisplaySettings,
    // Lex reorder is a board feature; Task opts in with its sortOrder field.
    reorder: {
      field: 'sortOrder',
      displays: [EntityViewDisplay.List],
      withinGroupOnly: true,
    },
  },
  // Job-scoped mounts (finance-pro/tasks, relations) keep the same policy.
  embedded: {
    list: {
      displayProperties: {
        allow: ['tags', 'type', 'target', 'evidence', 'dueDate', 'assignee', 'createdAt', 'updatedAt'],
      },
      // One-line v2 row (WBS-13010); reverting is these four lines back to the v1 row and evidence chip.
      rowRenderer: ListRowV2Renderer,
      rowHeight: 40,
      fixedProperties: ['evidence'],
      propertyPresentations: { evidence: resolveTaskEvidenceListIcon, dueDate: resolveTaskDueDateListProperty(), assignee: resolveTaskAssigneeListProperty },
      titleAdornment: resolveTaskTitleAdornment,
      parentProgress: resolveTaskParentProgress,
      nested: taskNestedList,
    },
    displaySettings: taskNestedDisplaySettings,
    reorder: {
      field: 'sortOrder',
      displays: [EntityViewDisplay.List],
      withinGroupOnly: true,
    },
  },
};

/**
 * Task board entity — list-first probe under finance-pro/tasks (parent-scoped job).
 *
 * Tasks are always parent-scoped (job / stage / contact / …). The data strategy requires
 * `parentId` or `entityIds`; standalone top-level task boards are not supported.
 */
export const taskManifest = registerManifest<Task>({
  entity: ActivityEntityName.Task,
  slug: 'task',

  surfaces: taskSurfaces,
  operations: {
    create: {
      surface: SurfaceType.Form,
      presentation: {
        mode: 'dialog',
        width: 'min(728px, calc(100vw - 40px))',
      },
      authorize: ({ injector }) => injector.get(RightsService).hasRights(TaskSecurityRight.ManageManualTasks),
    },
    edit: {
      surface: SurfaceType.Detail,
    },
    preview: {
      surface: SurfaceType.Detail,
    },
  },

  board: taskBoard,

  // Which toggles appear is the parent's call (`shareRelations`); the portal domain below omits sharing.
  shareable: true,
  parentOf: resolveTaskRecordParent,

  // A domain gets nothing by omission, so the contractor portal restates what it mounts (WBS-11934):
  // the same list and the detail surface to read and complete a task. Contractors never add tasks, so
  // the board hides Add outright rather than leaving the no-create-operation fallback to show it.
  domains: {
    [DomainType.ContractorPortal]: {
      surfaces: taskSurfaces,
      // A task page opened by its address needs the portal Tasks › View (WBS-12242).
      operations: {
        edit: { surface: SurfaceType.Detail, authorize: ({ injector }) => getPortalContractorSectionRights(injector.get(RightsService).role).tasks.view },
        preview: { surface: SurfaceType.Detail, authorize: ({ injector }) => getPortalContractorSectionRights(injector.get(RightsService).role).tasks.view },
      },
      board: {
        ...taskBoard,
        defaults: {
          ...taskBoard.defaults,
          header: { ...taskBoard.defaults?.header, hideAdd: true },
        },
      },
    },
    // Client Portal job Tasks (new) (WBS-12872): the same list, read only. Clients follow the work, never do it,
    // so a task opens only as a preview (no edit operation) and the board never offers Add.
    [DomainType.ClientPortal]: {
      surfaces: taskSurfaces,
      operations: {
        preview: {
          surface: SurfaceType.Detail,
          authorize: ({ injector }) => getPortalClientSectionRights(injector.get(RightsService).role).jobTasks.view,
          // The SOR detail's rail opens a sub-task through its own read (WBS-12873); the job Tasks tab keeps the plain read.
          loadRecord: ({ id, params, injector }) => hasLoadTask(params) ? params.loadTask(id) : readTask(injector.get(EntityDataServiceRaw), id),
        },
      },
      board: {
        ...taskBoard,
        defaults: {
          ...taskBoard.defaults,
          header: { ...taskBoard.defaults?.header, hideAdd: true },
        },
      },
    },
  },
});

/** Whether the opener passed its own task read (the SOR rail); the manifest hands surface params over untyped. */
function hasLoadTask(params: unknown): params is Required<Pick<TaskDetailParams, 'loadTask'>> {

  return typeof params === 'object' && params !== null && 'loadTask' in params && typeof params.loadTask === 'function';
}

/** The default record read, as the manifest service makes it when an operation declares no loader. */
function readTask(entityData: EntityDataServiceRaw, id: string): Promise<Task | null> {

  return firstValueFrom(entityData.getById<Task>(ActivityEntityName.Task, id))
    .then(response => response?.item ?? null)
    .catch(() => null);
}
